import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { NavLink } from 'react-router-dom';
import { DateTime } from 'luxon';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Divider from '@material-ui/core/Divider';
import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import Chip from '@material-ui/core/Chip';
import Link from '@material-ui/core/Link';
import TablePagination from '@material-ui/core/TablePagination';

import LoopIcon from '@material-ui/icons/Loop';
import AddIcon from '@material-ui/icons/Add';

import Loading from 'app/components/loaders/Loading';
import NewElectionDialog from 'app/components/election/NewElectionDialog';

import { paper } from 'app/styles/core';
import { container } from 'app/styles/new-layout';

import useApi from 'app/context/useApi';

const useStyles = makeStyles(theme => ({
    container: {
        ...container(theme),
        padding: theme.spacing(3)
    },
    paper: {
        ...paper(theme),
        position: 'relative'
    },
    marginBottom: {
        marginBottom: theme.spacing(3)
    },
    marginBottomSmall: {
        marginBottom: theme.spacing(1)
    },
    textSuccess: {
        color: theme.palette.success.main
    },
    textWarning: {
        color: theme.palette.warning.main
    },
    listItem: {
        display: 'block',
        marginBottom: theme.spacing(3)
    },
    empty: {
        paddingTop: theme.spacing(5),
        paddingBottom: theme.spacing(5),
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3)
    },
}));

export default function Elections(props) {

    const classes = useStyles();
    const api = useApi();

    const [ elections, setElections ] = useState([]);
    const [ newElectionDialogOpen, setNewElectionDialogOpen ] = useState(false);
    const [ page, setPage ] = useState(0);
    const [ rowsPerPage, setRowsPerPage ] = useState(10);
    const [ loading, setLoading ] = useState(false);

    // Getters -------------------------------------

    const getElections = () => {
        setLoading(true);

        api.get(`/admin/election`)
            .then(res => setElections(res.elections))
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        getElections();
    }, []);

    // Handlers ---------------------------------

    const handleChangePage = (event, newPage) => {
        setPage(newPage)
    };

    const handleChangeRowsPerPage = event => {
        setRowsPerPage(event.target.value)
    };

    // Dialog functions ---------------------------------

    const openNewElectionDialog = () => {
        setNewElectionDialogOpen(true)
    }

    const closeNewElectionDialog = () => {
        setNewElectionDialogOpen(false);
    }

    // Constants ---------------------------------

    const getStatusText = (e, variant) => {

        const s1 = (variant === 'voting') ? '' : 's';
        const s2 = (variant === 'voting') ? 's' : '';

        const st = DateTime.fromISO(e[`${variant}${s1}_start${s2}_at`]);
        const et = DateTime.fromISO(e[`${variant}${s1}_end${s2}_at`]);

        const now = DateTime.local()

        if (e.proposals_active && st.isValid && et.isValid) {
            if (st < now && et > now) {
                // Proposals Active
                return (
                    <Typography component="span" variant="subtitle2" className={classes.textSuccess}>
                        Active | Ends {et.toLocaleString(DateTime.DATETIME_FULL)}&nbsp;&nbsp;&nbsp;({et.toRelative()})
                    </Typography>
                )
            } else if (st > now) {
                // Proposals Upcoming
                return (
                    <Typography component="span" variant="subtitle2" color="primary">
                        Upcoming | Begins {st.toLocaleString(DateTime.DATETIME_FULL)}&nbsp;&nbsp;&nbsp;({st.toRelative()})
                    </Typography>
                )
            } else {
                // Proposals have past
                return (
                    <Typography component="span" variant="subtitle2" className={classes.textWarning}>
                        Closed | Ended {et.toLocaleString(DateTime.DATETIME_FULL)}&nbsp;&nbsp;&nbsp;({et.toRelative()})
                    </Typography>
                )
            }
        } else {
            return (
                <Typography component="span" variant="subtitle2" color="error">
                    Inactive
                </Typography>
            )
        }
    }

    const getRepeatLabel = e => {
        if (e.repeated) {
            return `Every ${e.repeat_amount} ${e.repeat_period}`
        } else {
            return 'Does Not Repeat'
        }
    }

    return (
        <div className={classes.container}>
            <Paper className={classes.paper}>
                <Loading isLoading={loading}/>
                <Grid
                    container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    className={classes.marginBottom}
                    >
                    <Grid item>
                        <Typography variant="h4" component="h2">
                            Elections
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Button
                            aria-label="New Election"
                            variant="contained"
                            onClick={openNewElectionDialog}
                            startIcon={<AddIcon />}
                            color="primary"
                            >
                            New Election
                        </Button>
                    </Grid>
                </Grid>
                <Divider />
                {(elections && elections.length > 0) ? (
                    <List>
                        {elections.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map( e => (
                            <ListItem
                                key={e.id}
                                button
                                component={NavLink}
                                to={`/admin/elections/${e.id}`}
                                className={classes.listItem}
                                divider
                                >
                                <Grid
                                    container
                                    spacing={3}
                                    alignItems="center"
                                    justify="space-between"
                                    wrap="nowrap"
                                    className={classes.marginBottomSmall}
                                    >
                                    <Grid item>
                                        <Typography variant="subtitle1" color="textSecondary">
                                            <strong>{e.formatted_name}</strong> - {e.description}
                                        </Typography>
                                    </Grid>
                                    <Grid item className={classes.gridItemRight}>
                                        <Chip
                                            icon={e.repeated ? <LoopIcon/> : null}
                                            label={getRepeatLabel(e)}
                                            color={e.repeated ? 'primary' : 'default'}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid
                                    container
                                    spacing={3}
                                    alignItems="center"
                                    justify="space-between"
                                    wrap="nowrap"
                                    >
                                    <Grid item>
                                        <Typography variant="subtitle2" color="textSecondary">
                                            Proposals:&nbsp;{getStatusText(e, 'proposal')}<br/>
                                            Recommendations:&nbsp;{getStatusText(e, 'recommendation')}<br/>
                                            Voting:&nbsp;{getStatusText(e, 'voting')}
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Typography variant="subtitle2" color="textSecondary">
                                            # of Proposals:&nbsp;<strong>{e.total_proposals}</strong><br/>
                                            # of Recommendations:&nbsp;<strong>{e.total_recommendations}</strong><br/>
                                            # of Votes:&nbsp;<strong>{e.total_votes}</strong>
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </ListItem>
                        ))}
                    </List>
                ) : (
                    <Typography
                        align="center"
                        color="textSecondary"
                        variant="subtitle2"
                        className={classes.empty}
                        >
                        You don't have any elections yet. Create your first election <Link onClick={openNewElectionDialog}>here</Link>.
                    </Typography>
                )}
                <Divider />
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    count={elections.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onChangePage={handleChangePage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                    component="div"
                />
            </Paper>
            <NewElectionDialog
                open={newElectionDialogOpen}
                onClose={closeNewElectionDialog}
            />
        </div>
    );
}
